"use client";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useReducedMotion } from "@/components/ui/reduce-motion";
import { AdminLoadingSpinner } from "@/components/admin/AdminComponents";
import { usePortfolio } from "@/hooks/usePortfolio";
import Image from "next/image";
import { LazyGlobe, GlobeFallback } from "@/components/ui/LazyLoad";

export default function AboutSection() {
  const prefersReducedMotion = useReducedMotion();
  const containerRef = useRef(null);
  const { data, loading, error } = usePortfolio();

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [0, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1, 0.8]);

  if (loading) {
    return (
      <section id="about" className="py-20">
        <div className="flex items-center justify-center min-h-[400px]">
          <AdminLoadingSpinner />
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="about" className="py-20">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-500 mb-4">Error Loading Profile</h2>
          <p className="text-gray-600 dark:text-gray-400">{error}</p>
        </div>
      </section>
    );
  }

  const { profile, skills } = data;
  const orbitingSkills = skills.slice(0, 8).map(skill => ({
    id: skill.id,
    src: skill.icon,
    alt: skill.name,
  }));

  return (
    <section id="about" className="py-12 sm:py-20" ref={containerRef}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          style={{ opacity, scale }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: prefersReducedMotion ? 0 : 0.5 }}
          viewport={{ once: true }}
          className="text-center space-y-4 mb-8 sm:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            About Me
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-8 lg:gap-16 items-center">
          {/* Image Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.5 }}
            viewport={{ once: true }}
            className="relative order-2 lg:order-1"
          >
            <div className="relative w-full aspect-square max-w-xl mx-auto">
              <LazyGlobe />
            </div>
          </motion.div>

          {/* Content Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: prefersReducedMotion ? 0 : 0.5 }}
            viewport={{ once: true }}
            className="space-y-6 order-1 lg:order-2 max-w-xl mx-auto"
          >
            <div className="space-y-4">
              <motion.h3 
                className="text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-white"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                viewport={{ once: true }}
              >
                Hi, I'm {profile?.name || "Nirmal"} 👋
              </motion.h3>
              <motion.p 
                className="text-gray-600 dark:text-gray-300 text-lg sm:text-xl font-light text-justify"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                viewport={{ once: true }}
              >
                {profile?.bio || "I'm a passionate full-stack developer based in Mumbai, India. With a strong foundation in web development and a keen eye for design, I create beautiful and functional web applications that provide exceptional user experiences."}
              </motion.p>
            </div>

            <div className="space-y-4">
              <motion.h4 
                className="text-lg sm:text-xl font-medium text-gray-900 dark:text-white"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                viewport={{ once: true }}
              >
               
              </motion.h4>
             
            </div>

            <motion.div 
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              viewport={{ once: true }}
            >
              <motion.a
                href="#contact"
                className="px-6 py-3 bg-blue-600 text-white rounded-lg shadow-md hover:bg-blue-700 hover:shadow-lg transition-all text-center font-semibold focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2"
                whileHover={{ scale: 1.07 }}
                whileTap={{ scale: 0.97 }}
                viewport={{ once: true }}
              >
                Get in Touch
              </motion.a>
              <motion.a
                href="/Nirmal_Darekar_Resume_ATS_Format.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="px-6 py-3 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-lg shadow-md hover:bg-gray-200 dark:hover:bg-gray-600 hover:shadow-lg transition-all text-center font-semibold focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2"
                whileHover={{ scale: 1.07 }}
                whileTap={{ scale: 0.97 }}
                viewport={{ once: true }}
              >
                Download Resume
              </motion.a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
} 